import { ReactNode, useMemo } from 'react';

import { CampaignProcess, CampaignSteps } from 'utils/constants/campaign';

import { BannerPreviewSidebar } from 'components/CpcFlexibleCreate/BannerPreviewSidebar/BannerPreviewSidebar';
import { PredictWindow } from 'components/CpcFlexibleCreate/PredictWindow/PredictWindow';
import { SourcesAndBudgetsSidebar } from 'components/CpcFlexibleCreate/SourcesAndBudgetsSidebar/SourcesAndBudgetsSidebar';

import { selectCampaignProcess } from 'store/models/createCampaign';
import { useSelector } from 'store/utils';

interface SideBarElementsProps {
    currentStep: CampaignSteps;
}

interface ReturnData {
    titleText: string;
    renderSidebarElements: (props: SideBarElementsProps) => React.ReactNode;
}

const CreateTitleTextsRedesign = {
    [CampaignSteps.First]: 'CampaignCreationWizardFlow.breadcrumbs.settings',
    [CampaignSteps.Second]: 'CampaignCreationWizardFlow.breadcrumbs.audience',
    [CampaignSteps.Third]: 'CampaignCreationWizardFlow.breadcrumbs.budget',
};

const CopyTitleTextsRedesign = {
    [CampaignSteps.First]: 'AdCreatePage.title.copy',
    [CampaignSteps.Second]: 'CampaignCreationWizardFlow.breadcrumbs.audience',
    [CampaignSteps.Third]: 'CampaignCreationWizardFlow.breadcrumbs.budget',
};

const EditTitleTextsRedesign = {
    [CampaignSteps.First]: 'AdCreatePage.title.edit',
    [CampaignSteps.Second]: 'CampaignCreationWizardFlow.breadcrumbs.budget',
    [CampaignSteps.Third]: '',
};

export const CPC_FLEXIBLE_TITLE_TEXTS_REDESIGN = {
    [CampaignProcess.Create]: CreateTitleTextsRedesign,
    [CampaignProcess.Edit]: EditTitleTextsRedesign,
    [CampaignProcess.Copy]: CopyTitleTextsRedesign,
};

const renderBannerPreviewSidebar = () => <BannerPreviewSidebar />;
const renderPredictWindow = () => <PredictWindow />;
const renderThirdStepSidebar = () => <SourcesAndBudgetsSidebar />;

const COPY_CREATE_SIDEBAR_STEPS_FILLING = {
    [CampaignSteps.First]: renderBannerPreviewSidebar,
    [CampaignSteps.Second]: renderPredictWindow,
    [CampaignSteps.Third]: renderThirdStepSidebar,
};

const EDIT_SIDEBAR_STEPS_FILLING = {
    [CampaignSteps.First]: renderPredictWindow,
    [CampaignSteps.Second]: renderThirdStepSidebar,
    [CampaignSteps.Third]: () => <></>,
};

type SidebarElements = (props: SideBarElementsProps) => ReactNode;

const CreateSidebarElements: SidebarElements = ({ currentStep }: SideBarElementsProps) =>
    COPY_CREATE_SIDEBAR_STEPS_FILLING[currentStep]();
const CopySidebarElements: SidebarElements = ({ currentStep }: SideBarElementsProps) =>
    COPY_CREATE_SIDEBAR_STEPS_FILLING[currentStep]();
const EditSidebarElements: SidebarElements = ({ currentStep }: SideBarElementsProps) =>
    EDIT_SIDEBAR_STEPS_FILLING[currentStep]();

const SIDEBAR_ELEMENTS = {
    [CampaignProcess.Create]: CreateSidebarElements,
    [CampaignProcess.Edit]: EditSidebarElements,
    [CampaignProcess.Copy]: CopySidebarElements,
};

export const useCpcFlexibleCreateData = (): ReturnData => {
    const campaignProcess = useSelector(selectCampaignProcess);
    const { currentStep } = useSelector((state) => state.createCampaign);

    const step = String(currentStep) as CampaignSteps;
    const titleTexts = CPC_FLEXIBLE_TITLE_TEXTS_REDESIGN;

    return useMemo(
        () => ({
            titleText: titleTexts[campaignProcess][step],
            renderSidebarElements: SIDEBAR_ELEMENTS[campaignProcess],
        }),
        [campaignProcess, step, titleTexts]
    );
};
